import { IApiBaseProps } from '@common/api.models';
import { isValidApiBaseProps } from '@common/api.services';
import { FETCH_QUERIES_NAME, MUTATION_KEYS } from '@common/constants';
import { AxiosErrorResponse } from '@core/error.models';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { useMutation, useQuery } from '@tanstack/react-query';
import { IItemResponse } from '@gcl/react-core';
import {
  ICancelProjectMasterScheduleRequest,
  ILockProjectMasterScheduleRequest,
  IMasterScheduleDetail,
} from './masterScheduleDetail.models';
import {
  cancelMasterScheduleAsync,
  getMasterScheduleDetailAsync,
  uncancelMasterScheduleAsync,
  lockMasterScheduleAsync,
  unlockMasterScheduleAsync,
} from './masterScheduleDetail.services';

export const useFetchMasterScheduleDetail = (props: IApiBaseProps, id: number | undefined, enabled = true) => {
  const shouldEnable = isPositiveNumber(id) && isValidApiBaseProps(props) && enabled;
  return useQuery<IMasterScheduleDetail, AxiosErrorResponse>(
    [props.projectId, FETCH_QUERIES_NAME.MASTER_SCHEDULE_DETAIL, id],
    async () => {
      if (id === undefined || id === null) {
        throw new Error('Id must be provided');
      }
      const results = await getMasterScheduleDetailAsync(id, props);
      if (!results.item) throw new Error('Failed to get T1 detail');
      return results.item;
    },
    {
      enabled: shouldEnable,

      keepPreviousData: true,
    },
  );
};

export const useCancelMasterSchedule = () => {
  return useMutation<IItemResponse<IMasterScheduleDetail>, AxiosErrorResponse, ICancelProjectMasterScheduleRequest>(
    [MUTATION_KEYS.CANCEL_MASTER_SCHEDULE],
    async (cancelRequest: ICancelProjectMasterScheduleRequest) => {
      const result = await cancelMasterScheduleAsync(cancelRequest);
      return result;
    },
  );
};

export const useUncancelMasterSchedule = () => {
  return useMutation<IItemResponse<IMasterScheduleDetail>, AxiosErrorResponse, ICancelProjectMasterScheduleRequest>(
    [MUTATION_KEYS.UNCANCEL_MASTER_SCHEDULE],
    async (cancelRequest: ICancelProjectMasterScheduleRequest) => {
      const result = await uncancelMasterScheduleAsync(cancelRequest);
      return result;
    },
  );
};

export const useLockMasterSchedule = () => {
  return useMutation<IItemResponse<IMasterScheduleDetail>, AxiosErrorResponse, ILockProjectMasterScheduleRequest>(
    [MUTATION_KEYS.LOCK_MASTER_SCHEDULE],
    async (lockRequest: ILockProjectMasterScheduleRequest) => {
      const result = await lockMasterScheduleAsync(lockRequest);
      return result;
    },
  );
};

export const useUnlockMasterSchedule = () => {
  return useMutation<IItemResponse<IMasterScheduleDetail>, AxiosErrorResponse, ILockProjectMasterScheduleRequest>(
    [MUTATION_KEYS.UNLOCK_MASTER_SCHEDULE],
    async (lockRequest: ILockProjectMasterScheduleRequest) => {
      const result = await unlockMasterScheduleAsync(lockRequest);
      return result;
    },
  );
};
